/**
 * Turns the live working tree (vs. its server snapshot) into a payload for
 * POST /api/camera-model/changeset/ — see manager-api/docs/api/camera-model-crud-api.md.
 *
 * Rules:
 *   - TEMP entity (no server counterpart) ⇒ create op (its TEMP public_id is the client_ref).
 *   - TEMP + localDeleted ⇒ no-op (the entity never reached the server).
 *   - server entity + localDeleted ⇒ delete op (its updated_at becomes the OCC token).
 *   - server entity + field diff ⇒ update op (patch = only changed fields + their updated_at).
 *   - server entity + no diff ⇒ no op.
 *
 * Ordering: parents-before-children for creates; updates next; deletes last.
 * Cascade deletes are not enumerated — a `delete capture_device` is sufficient
 * for the server to wipe its children.
 */

import { diffInternals } from "./diff";

const { fkValue, normalizedEqual, normalizeResolution, RESOLUTION_FIELDS } = diffInternals;

const DEVICE_CREATE_FIELDS = [
  "store",
  "name",
  "brand",
  "transport",
  "host",
  "rtsp_port",
  "api_port",
  "rtsp_port_redirected",
  "api_port_redirected",
  "username",
  "password",
  "caching",
  "set_date",
];
const DEVICE_UPDATE_FIELDS = [
  "name",
  "brand",
  "transport",
  "host",
  "rtsp_port",
  "api_port",
  "rtsp_port_redirected",
  "api_port_redirected",
  "username",
  "password",
  "caching",
  "set_date",
];

const CAMERA_CREATE_FIELDS = [
  "slot_number",
  "stream_quality",
  "url_mode",
  "rtsp_url_suffix",
  "protocol",
  "inference_machine",
  "alert_channel",
  "depth_of_field",
  "is_active",
];
const CAMERA_UPDATE_FIELDS = CAMERA_CREATE_FIELDS;

const VIEW_CREATE_FIELDS = [
  "view_number",
  "view_name",
  "view_area",
  "resolution",
  "body_sensitivity",
  "bag_sensitivity",
  "relevance",
  "is_active",
  "body_theft",
  "gesture_into_body",
  "product_in_personal_bag",
  "product_into_backpack",
  "suspicious_bag",
  "product_into_stroller",
  "client_normal_bag",
  "store_normal_bag",
  "deblistering",
  "consumption",
  "burst_shot",
  "other_suspicious",
];
const VIEW_UPDATE_FIELDS = VIEW_CREATE_FIELDS;

const DEVICE_FK_FIELDS = new Set(["brand"]);
const CAMERA_FK_FIELDS = new Set(["inference_machine", "alert_channel", "depth_of_field"]);
const VIEW_FK_FIELDS = new Set(["body_sensitivity", "bag_sensitivity", "relevance"]);

function normalize(value, isFk, isResolution) {
  if (isFk) return fkValue(value);
  if (isResolution) return normalizeResolution(value);
  return value;
}

// The write accepts a frame only as `{width, height}`. Anything else (null on a new
// view, a bare PK from a fixture or an older payload) is left out rather than sent,
// so one malformed value cannot 400 the whole changeset.
const isSendableFrame = (value) =>
  !!value && typeof value === "object" && Number.isFinite(value.width) && Number.isFinite(value.height);

function pickFields(entity, fields, fkFields) {
  const out = {};
  fields.forEach((field) => {
    const raw = entity[field];
    if (raw === undefined) return;
    const isResolution = RESOLUTION_FIELDS.has(field);
    if (isResolution && !isSendableFrame(raw)) return;
    out[field] = normalize(raw, fkFields.has(field), isResolution);
  });
  return out;
}

function diffFields(serverEntity, workingEntity, fields, fkFields) {
  const out = {};
  fields.forEach((field) => {
    const isFk = fkFields.has(field);
    const isResolution = RESOLUTION_FIELDS.has(field);
    if (normalizedEqual(serverEntity[field], workingEntity[field], isFk, isResolution)) return;
    if (isResolution && !isSendableFrame(workingEntity[field])) return;
    out[field] = normalize(workingEntity[field], isFk, isResolution);
  });
  return out;
}

let opCounter = 0;
function nextOpId() {
  opCounter += 1;
  return `op_${Date.now()}_${opCounter}`;
}

function indexServerTree(serverTree) {
  const devices = new Map();
  const cameras = new Map();
  const views = new Map();
  (serverTree || []).forEach((d) => {
    devices.set(d.public_id, d);
    (d.cameras || []).forEach((c) => {
      cameras.set(c.public_id, c);
      (c.views || []).forEach((v) => views.set(v.public_id, v));
    });
  });
  return { devices, cameras, views };
}

export default function buildChangeset(serverTree, workingTree) {
  const server = indexServerTree(serverTree);
  const creates = [];
  const updates = [];
  const deletes = [];

  (workingTree || []).forEach((device) => {
    const serverDevice = server.devices.get(device.public_id);
    const deviceCreatedLocally = !serverDevice;
    const deviceDeletedLocally = !!device.localDeleted;

    // TEMP + locally deleted ⇒ never reached the server, nothing to do (and skip children).
    if (deviceCreatedLocally && deviceDeletedLocally) return;

    // Server device, locally deleted ⇒ one delete op; children are cascaded server-side.
    if (!deviceCreatedLocally && deviceDeletedLocally) {
      deletes.push({
        op_id: nextOpId(),
        op: "delete",
        type: "capture_device",
        public_id: device.public_id,
        version: device.updated_at,
      });
      return;
    }

    if (deviceCreatedLocally) {
      creates.push({
        op_id: nextOpId(),
        op: "create",
        type: "capture_device",
        client_ref: device.public_id,
        data: pickFields(device, DEVICE_CREATE_FIELDS, DEVICE_FK_FIELDS),
      });
    } else {
      const patch = diffFields(serverDevice, device, DEVICE_UPDATE_FIELDS, DEVICE_FK_FIELDS);
      if (Object.keys(patch).length > 0) {
        updates.push({
          op_id: nextOpId(),
          op: "update",
          type: "capture_device",
          public_id: device.public_id,
          version: device.updated_at,
          data: patch,
        });
      }
    }

    // Cameras inherit the device's public_id (TEMP or real) as parent_ref.
    const deviceRef = device.public_id;
    (device.cameras || []).forEach((camera) => {
      const serverCamera = server.cameras.get(camera.public_id);
      const cameraCreatedLocally = !serverCamera;
      const cameraDeletedLocally = !!camera.localDeleted;

      if (cameraCreatedLocally && cameraDeletedLocally) return;

      if (!cameraCreatedLocally && cameraDeletedLocally) {
        deletes.push({
          op_id: nextOpId(),
          op: "delete",
          type: "camera",
          public_id: camera.public_id,
          version: camera.updated_at,
        });
        return;
      }

      if (cameraCreatedLocally) {
        creates.push({
          op_id: nextOpId(),
          op: "create",
          type: "camera",
          client_ref: camera.public_id,
          parent_ref: deviceRef,
          data: pickFields(camera, CAMERA_CREATE_FIELDS, CAMERA_FK_FIELDS),
        });
      } else {
        const patch = diffFields(serverCamera, camera, CAMERA_UPDATE_FIELDS, CAMERA_FK_FIELDS);
        if (Object.keys(patch).length > 0) {
          updates.push({
            op_id: nextOpId(),
            op: "update",
            type: "camera",
            public_id: camera.public_id,
            version: camera.updated_at,
            data: patch,
          });
        }
      }

      const cameraRef = camera.public_id;
      (camera.views || []).forEach((view) => {
        const serverView = server.views.get(view.public_id);
        const viewCreatedLocally = !serverView;
        const viewDeletedLocally = !!view.localDeleted;

        if (viewCreatedLocally && viewDeletedLocally) return;

        if (!viewCreatedLocally && viewDeletedLocally) {
          deletes.push({
            op_id: nextOpId(),
            op: "delete",
            type: "view",
            public_id: view.public_id,
            version: view.updated_at,
          });
          return;
        }

        if (viewCreatedLocally) {
          creates.push({
            op_id: nextOpId(),
            op: "create",
            type: "view",
            client_ref: view.public_id,
            parent_ref: cameraRef,
            data: pickFields(view, VIEW_CREATE_FIELDS, VIEW_FK_FIELDS),
          });
        } else {
          const patch = diffFields(serverView, view, VIEW_UPDATE_FIELDS, VIEW_FK_FIELDS);
          if (Object.keys(patch).length > 0) {
            updates.push({
              op_id: nextOpId(),
              op: "update",
              type: "view",
              public_id: view.public_id,
              version: view.updated_at,
              data: patch,
            });
          }
        }
      });
    });
  });

  return { ops: [...creates, ...updates, ...deletes] };
}
